/**
 * SONAUTO — customer locale negotiation.
 *
 * Runs on every QR-ordering request in the Worker, so it is pure, allocation
 * light and MUST NOT throw: a hostile or broken `Accept-Language` header is a
 * completely normal input, not an error condition. Every parse path is
 * defensive and falls through to the next signal instead of raising.
 *
 * ---------------------------------------------------------------------------
 * PRECEDENCE (product spec)
 * ---------------------------------------------------------------------------
 *   1. GUEST_CHOICE   — the guest tapped the language switcher. The session
 *                       locale is then flagged `guestLocked` and is NEVER
 *                       overridden by anything below, for the life of the
 *                       session. This is the whole point: a Russian guest who
 *                       picks Русский must not be flipped back to Vietnamese
 *                       by a stored profile preference or a phone that sends
 *                       `Accept-Language: vi`.
 *   2. STORED         — a preference persisted against the customer profile.
 *   3. SESSION        — a locale already attached to this QR session by an
 *                       earlier negotiation (sticky, but a weaker signal than
 *                       an explicit stored preference).
 *   4. BROWSER        — `Accept-Language`, q-values honoured.
 *   5. BRANCH_DEFAULT — `branch_locales.is_default` / `tenant_locales`.
 *   6. FALLBACK       — vi-VN, the source language.
 *
 * A candidate only wins if the branch actually has that locale enabled; an
 * enabled-but-unknown tag (present in D1, missing from the catalogue) is
 * ignored because nothing downstream could format it.
 */

import type { LocaleInfo } from './locales.js';
import { DEFAULT_LOCALE, canonicalizeLocale, getLocaleInfo, languageOf } from './locales.js';

export type LocaleSource =
  | 'GUEST_CHOICE'
  | 'SESSION'
  | 'STORED'
  | 'BROWSER'
  | 'BRANCH_DEFAULT'
  | 'FALLBACK';

export interface NegotiatedLocale {
  readonly locale: string;
  readonly source: LocaleSource;
}

/** Session-carried locale. `guestLocked` marks an explicit guest choice. */
export interface SessionLocaleState {
  readonly locale: string | null | undefined;
  /** True once the guest used the language switcher. */
  readonly guestLocked: boolean;
}

export interface NegotiateLocaleInput {
  /** Raw `Accept-Language` request header. Any garbage is tolerated. */
  readonly acceptLanguage?: string | null;
  /** Persisted customer preference (customer profile / device store). */
  readonly storedPreference?: string | null;
  /** Locale on the QR session; a bare string is treated as NOT guest-locked. */
  readonly sessionLocale?: SessionLocaleState | string | null;
  /** Locales the branch has enabled. Empty/omitted ⇒ source locale only. */
  readonly enabledLocales?: readonly string[];
  /** `branch_locales.is_default` (falling back to `tenant_locales`). */
  readonly branchDefault?: string | null;
}

// ---------------------------------------------------------------------------
// Accept-Language parsing (RFC 9110 §12.5.4 / RFC 4647 shape)
// ---------------------------------------------------------------------------

export interface AcceptLanguageTag {
  /** Canonicalised tag, e.g. `zh-CN`. `*` is preserved verbatim. */
  readonly tag: string;
  /** ISO 639-1 part, `''` for the `*` wildcard. */
  readonly languageCode: string;
  /** Region subtag, `null` when the tag is language-only or `*`. */
  readonly regionCode: string | null;
  /** Quality value in [0, 1]. Entries with q=0 are dropped by the parser. */
  readonly quality: number;
  /** Position in the original header, used as the stable tie-break. */
  readonly position: number;
}

// `en`, `en-US`, `zh-Hans-CN`, `x-klingon`, `*`
const TAG_RE = /^(?:\*|[A-Za-z]{1,8}(?:-[A-Za-z0-9]{1,8})*)$/;
const MAX_HEADER_LENGTH = 512;
const MAX_TAGS = 32;

/**
 * Parse `Accept-Language` into tags ordered by descending quality, ties broken
 * by header position (RFC says earlier wins).
 *
 * Robustness rules, all deliberate:
 *   - never throws, for any input, including non-strings;
 *   - oversized headers are truncated (`MAX_HEADER_LENGTH`) and the tag count
 *     capped (`MAX_TAGS`) so a hostile header cannot burn CPU;
 *   - a malformed entry is skipped, the rest of the header still parses;
 *   - `q` that is missing/unparseable defaults to 1; `q=0` means "not
 *     acceptable" and the entry is dropped entirely.
 */
export function parseAcceptLanguage(header: string | null | undefined): readonly AcceptLanguageTag[] {
  if (typeof header !== 'string') return [];
  const raw = header.slice(0, MAX_HEADER_LENGTH);
  if (raw.trim() === '') return [];

  const out: AcceptLanguageTag[] = [];
  let position = 0;

  for (const chunk of raw.split(',')) {
    if (out.length >= MAX_TAGS) break;
    const parts = chunk.split(';');
    const tagRaw = (parts[0] ?? '').trim();
    if (tagRaw === '' || !TAG_RE.test(tagRaw)) continue;

    let quality = 1;
    for (const param of parts.slice(1)) {
      const eq = param.indexOf('=');
      if (eq === -1) continue;
      if (param.slice(0, eq).trim().toLowerCase() !== 'q') continue;
      const parsed = Number.parseFloat(param.slice(eq + 1).trim());
      quality = Number.isFinite(parsed) ? Math.min(1, Math.max(0, parsed)) : 1;
    }
    if (quality <= 0) continue; // q=0 ⇒ explicitly unacceptable

    const tag = tagRaw === '*' ? '*' : canonicalizeLocale(tagRaw);
    if (tag === '') continue;

    out.push({
      tag,
      languageCode: tag === '*' ? '' : languageOf(tag),
      regionCode: tag === '*' ? null : regionSubtag(tag),
      quality,
      position: position++,
    });
  }

  return out.sort((a, b) => b.quality - a.quality || a.position - b.position);
}

function regionSubtag(tag: string): string | null {
  const parts = tag.split('-');
  const region = parts.slice(1).find((p) => /^[A-Z]{2}$/.test(p));
  return region ?? null;
}

// ---------------------------------------------------------------------------
// Matching
// ---------------------------------------------------------------------------

/**
 * Resolve a requested tag against the enabled set.
 *
 * Two passes, in this order:
 *   1. exact tag match (case-insensitive)  — `zh-CN` → `zh-CN`
 *   2. language-only match                 — `zh` → `zh-CN`, `en-GB` → `en-US`
 *
 * Pass 2 is what lets a phone that only sends `zh` or a British guest sending
 * `en-GB` land on the branch's `zh-CN` / `en-US` bundle. Within a pass the
 * candidate with the lowest catalogue `sortOrder` wins so the choice is
 * deterministic (never "whatever D1 returned first").
 */
export function matchLocale(requested: string | null | undefined, enabled: readonly string[]): string | null {
  const want = canonicalizeLocale(requested);
  if (want === '' || want === '*') return null;
  const candidates = resolveEnabled(enabled);
  if (candidates.length === 0) return null;

  const exact = candidates.find((c) => c.locale.toLowerCase() === want.toLowerCase());
  if (exact !== undefined) return exact.locale;

  const lang = languageOf(want);
  if (lang === '') return null;
  const byLanguage = candidates.filter((c) => c.languageCode === lang);
  return byLanguage[0]?.locale ?? null;
}

/**
 * Enabled tags → catalogue entries, deduped, unknown tags dropped, ordered by
 * `sortOrder`. When nothing usable is enabled the source locale is used so the
 * customer surface always renders something.
 */
function resolveEnabled(enabled: readonly string[] | undefined): readonly LocaleInfo[] {
  const seen = new Set<string>();
  const out: LocaleInfo[] = [];
  for (const tag of enabled ?? []) {
    const info = getLocaleInfo(tag);
    if (info === undefined || seen.has(info.locale)) continue;
    seen.add(info.locale);
    out.push(info);
  }
  if (out.length === 0) {
    const fallback = getLocaleInfo(DEFAULT_LOCALE);
    if (fallback !== undefined) out.push(fallback);
  }
  return out.sort((a, b) => a.sortOrder - b.sortOrder || a.locale.localeCompare(b.locale));
}

function readSession(value: NegotiateLocaleInput['sessionLocale']): SessionLocaleState {
  if (typeof value === 'string') return { locale: value, guestLocked: false };
  if (value === null || value === undefined) return { locale: null, guestLocked: false };
  return { locale: value.locale, guestLocked: value.guestLocked === true };
}

// ---------------------------------------------------------------------------
// Negotiation
// ---------------------------------------------------------------------------

/**
 * Pick the locale the customer surface renders in.
 *
 * Pure and total: for ANY input — including a header of `"};DROP TABLE"`,
 * `"q=q=q"` or 10 KB of junk — this returns a usable locale from the enabled
 * set and never throws.
 */
export function negotiateLocale(input: NegotiateLocaleInput): NegotiatedLocale {
  const enabled = resolveEnabled(input.enabledLocales);
  const enabledTags = enabled.map((e) => e.locale);
  const session = readSession(input.sessionLocale);

  // 1. GUEST_CHOICE — explicit, sticky, never overridden.
  //    If the branch has since disabled that locale we cannot serve it, so we
  //    fall through rather than render an empty bundle.
  if (session.guestLocked) {
    const chosen = matchLocale(session.locale, enabledTags);
    if (chosen !== null) return { locale: chosen, source: 'GUEST_CHOICE' };
  }

  // 2. STORED — customer profile / device preference.
  const stored = matchLocale(input.storedPreference, enabledTags);
  if (stored !== null) return { locale: stored, source: 'STORED' };

  // 3. SESSION — sticky value from an earlier negotiation on this session.
  if (!session.guestLocked) {
    const sticky = matchLocale(session.locale, enabledTags);
    if (sticky !== null) return { locale: sticky, source: 'SESSION' };
  }

  // 4. BROWSER — Accept-Language, q-ordered.
  for (const tag of parseAcceptLanguage(input.acceptLanguage)) {
    if (tag.tag === '*') continue; // wildcard carries no preference
    const matched = matchLocale(tag.tag, enabledTags);
    if (matched !== null) return { locale: matched, source: 'BROWSER' };
  }

  // 5. BRANCH_DEFAULT.
  const branch = matchLocale(input.branchDefault, enabledTags);
  if (branch !== null) return { locale: branch, source: 'BRANCH_DEFAULT' };

  // 6. FALLBACK — vi-VN if enabled, otherwise the first enabled locale so the
  //    guest is never handed a bundle the branch does not publish.
  const sourceEnabled = enabledTags.find((t) => t.toLowerCase() === DEFAULT_LOCALE.toLowerCase());
  return { locale: sourceEnabled ?? enabledTags[0] ?? DEFAULT_LOCALE, source: 'FALLBACK' };
}

/**
 * Build the session state after the guest taps the language switcher.
 * Returns `null` when the requested locale is not enabled, so the caller can
 * reject the request instead of silently locking an unrenderable locale.
 */
export function lockGuestChoice(
  requested: string | null | undefined,
  enabledLocales: readonly string[],
): SessionLocaleState | null {
  const matched = matchLocale(requested, enabledLocales);
  return matched === null ? null : { locale: matched, guestLocked: true };
}
